'use client'

import React from 'react'
import Link from 'next/link'
import { useSession } from 'next-auth/react'
import { usePathname, useSearchParams } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { NotificationCenter } from '@/components/NotificationCenter'
import { openCommandPalette } from '@/components/CommandPalette'
import { useSidebar } from '@/components/layout/Sidebar'
import { useAI } from '@/context/AIContext'
import { useBreadcrumbTitle } from '@/context/BreadcrumbContext'
import { buildBreadcrumbs } from '@/lib/breadcrumbs'
import { AI_MANAGER_NAME } from '@/lib/naming'
import { DEMO_FIRST_NAME, DEMO_INITIALS, DEMO_ROLE_LINE } from '@/lib/demo-identity'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

/** The search field: about 250px, "Search..." and the Cmd K hint. Click opens the palette. */
function SearchField() {
  return (
    <button
      type='button'
      onClick={openCommandPalette}
      className={cn(
        'group flex h-9 w-[250px] items-center gap-2 px-3',
        'rounded-[10px] border border-hairline bg-tab-track',
        'text-sm text-muted-foreground',
        'transition-colors hover:border-brand-cornflower/50 hover:bg-card',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower'
      )}
      aria-label='Search, or open the command palette (Command K)'
    >
      <span className='flex-1 text-left'>Search...</span>
      <kbd
        aria-hidden='true'
        className='rounded border border-hairline bg-card px-1.5 text-xs font-medium text-muted-foreground'
      >
        ⌘K
      </kbd>
    </button>
  )
}

/** The AI Manager: a solid navy pill, "AI Manager" with its shortcut. */
function AIManagerTrigger() {
  const { openManager, hasPendingSuggestions, isManagerOpen } = useAI()

  return (
    <button
      type='button'
      onClick={openManager}
      className={cn(
        'relative flex h-9 items-center gap-1.5 rounded-[10px] bg-primary px-3.5',
        'text-sm font-medium text-white',
        'transition-colors hover:bg-brand-navy-light',
        isManagerOpen && 'ring-2 ring-brand-cornflower ring-offset-2',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower focus-visible:ring-offset-2'
      )}
      aria-label={`Open ${AI_MANAGER_NAME}`}
    >
      <Icons.sparkles className='h-3.5 w-3.5' strokeWidth={1.5} aria-hidden='true' />
      <span className='hidden sm:inline'>{AI_MANAGER_NAME}</span>
      <span className='hidden text-xs text-white/75 sm:inline' aria-hidden='true'>
        ⌘J
      </span>
      {hasPendingSuggestions && (
        <span
          aria-hidden='true'
          className='absolute -right-1 -top-1 h-3 w-3 animate-pulse rounded-full border-2 border-card bg-brand-cornflower'
        />
      )}
    </button>
  )
}

/** The avatar and its menu. The first name sits beside the avatar on the briefing only. */
function UserMenu({ showName }: { showName: boolean }) {
  const { data: session } = useSession()
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''

  if (!session?.user) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          className='group flex items-center gap-2 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower focus-visible:ring-offset-2'
          aria-label={`${DEMO_FIRST_NAME}, account menu`}
        >
          <span className='flex h-8 w-8 items-center justify-center rounded-full bg-brand-navy text-xs font-semibold text-white'>
            {DEMO_INITIALS}
          </span>
          {showName && (
            <span className='hidden text-sm font-medium text-foreground sm:inline'>
              {DEMO_FIRST_NAME}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align='end' className='w-64'>
        <div className='border-b border-border/50 px-3 py-3'>
          <p className='text-sm font-semibold text-foreground'>{DEMO_FIRST_NAME}</p>
          <p className='text-sm text-muted-foreground'>{DEMO_ROLE_LINE}</p>
          {session.user.email && (
            <p className='mt-1 truncate text-xs text-muted-foreground'>{session.user.email}</p>
          )}
        </div>
        <div className='py-1'>
          <DropdownMenuItem asChild className='gap-3 rounded-lg px-3 py-2.5'>
            <Link href='/settings'>
              <Icons.settings className='h-4 w-4 text-muted-foreground' strokeWidth={1.5} aria-hidden='true' />
              <span>Your settings</span>
            </Link>
          </DropdownMenuItem>
        </div>
        <DropdownMenuSeparator />
        <div className='py-1'>
          <DropdownMenuItem
            onClick={() => {
              window.location.href = `${basePath}/api/auth/logout`
            }}
            destructive
            className='gap-3 rounded-lg px-3 py-2.5'
          >
            <Icons.logout className='h-4 w-4' strokeWidth={1.5} aria-hidden='true' />
            <span>Sign out</span>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** The trail itself. `search` is the query string; null until it is known. */
function BreadcrumbTrail({ search }: { search: string | null }) {
  const pathname = usePathname() ?? '/'
  const pageTitle = useBreadcrumbTitle()
  const crumbs = buildBreadcrumbs(pathname, pageTitle, search)
  const last = crumbs[crumbs.length - 1]
  return (
    <>
      <nav aria-label='Breadcrumb' className='hidden min-w-0 items-center gap-1.5 text-sm sm:flex'>
        {crumbs.map((crumb, index) => (
          <React.Fragment key={`${crumb.label}-${index}`}>
            {index > 0 && (
              <span aria-hidden='true' className='text-muted-foreground'>
                ›
              </span>
            )}
            {crumb.href ? (
              <Link
                href={crumb.href}
                className='shrink-0 rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower'
              >
                {crumb.label}
              </Link>
            ) : (
              <span
                aria-current='page'
                title={crumb.label}
                className='truncate font-semibold text-foreground'
              >
                {crumb.label}
              </span>
            )}
          </React.Fragment>
        ))}
      </nav>
      {/* Phones show only the last crumb. */}
      <span className='truncate text-sm font-semibold text-foreground sm:hidden'>{last.label}</span>
    </>
  )
}

/** Reads the query for the one trail that depends on it (the Context graph tab). */
function BreadcrumbTrailWithQuery() {
  const params = useSearchParams()
  return <BreadcrumbTrail search={params?.toString() ?? null} />
}

interface HeaderProps {
  onOpenMobileMenu?: () => void
}

/**
 * The floating white bar: breadcrumb rooted at Command Briefing, the search
 * field, the AI Manager pill, the bell and the avatar. 52px tall, 12px from
 * the top and sides, 14px radius (plan 13 section 8.3).
 */
export function Header({ onOpenMobileMenu }: HeaderProps) {
  const pathname = usePathname() ?? '/'
  const { isCollapsed } = useSidebar()

  return (
    <header
      role='banner'
      className={cn(
        'fixed right-3 top-3 z-sticky',
        'left-3',
        isCollapsed ? 'md:left-[calc(4rem+0.75rem)]' : 'md:left-[calc(16rem+0.75rem)]',
        'rounded-[14px] border border-hairline bg-card shadow-float',
        'flex h-[52px] items-center justify-between gap-3 px-4 lg:px-5',
        'transition-[left] duration-300 ease-out'
      )}
    >
      {/* Left: menu button on phones, then the breadcrumb */}
      <div className='flex min-w-0 items-center gap-2'>
        <button
          type='button'
          onClick={onOpenMobileMenu}
          className='-ml-1 flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower md:hidden'
          aria-label='Open navigation menu'
        >
          <Icons.menu className='h-5 w-5' strokeWidth={1.5} aria-hidden='true' />
        </button>

        <React.Suspense fallback={<BreadcrumbTrail search={null} />}>
          <BreadcrumbTrailWithQuery />
        </React.Suspense>
      </div>

      {/* Right: search, AI Manager, bell, avatar */}
      <div className='flex shrink-0 items-center gap-2 sm:gap-3'>
        <div className='hidden lg:block'>
          <SearchField />
        </div>
        <button
          type='button'
          onClick={openCommandPalette}
          className='flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower lg:hidden'
          aria-label='Search'
        >
          <Icons.search className='h-5 w-5' strokeWidth={1.5} aria-hidden='true' />
        </button>

        <AIManagerTrigger />
        <NotificationCenter />
        <UserMenu showName={pathname === '/'} />
      </div>
    </header>
  )
}
